/**
 * CapacitorAuthContext - Simple Auth Context for iOS
 *
 * Provides a React context that mimics Clerk's useAuth and useUser hooks
 * for native Capacitor apps.
 */

import { createContext, useContext, useState, useEffect } from 'react';

// Parse JWT token to extract payload (without verification - server will verify)
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('[CapacitorAuth] Failed to parse JWT:', e);
    return null;
  }
}

const CapacitorAuthContext = createContext(null);

export function CapacitorAuthProvider({ children, token, sessionId, onSignOut }) {
  const [user, setUser] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    console.log('[CapacitorAuth] Initializing with token:', token ? `${token.substring(0, 30)}...` : 'none');
    console.log('[CapacitorAuth] Session ID:', sessionId);

    if (token) {
      const payload = parseJwt(token);
      console.log('[CapacitorAuth] JWT payload:', JSON.stringify(payload, null, 2));

      // Always create a user object if we have a token
      const userId = payload?.sub || payload?.userId || sessionId || 'capacitor-user';

      setUser({
        id: userId,
        firstName: payload?.firstName || payload?.first_name || '',
        lastName: payload?.lastName || payload?.last_name || '',
        email: payload?.email || (payload?.emails && payload.emails[0]) || '',
        imageUrl: payload?.imageUrl || payload?.image_url || payload?.picture || '',
        fullName: payload?.name || `${payload?.firstName || ''} ${payload?.lastName || ''}`.trim() || 'Capacitor User',
        primaryEmailAddress: payload?.email ? { emailAddress: payload.email } : null,
      });
      console.log('[CapacitorAuth] User set successfully');
    } else {
      console.log('[CapacitorAuth] No token provided');
    }
    setIsLoaded(true);
  }, [token, sessionId]);

  const getToken = async () => {
    return token;
  };

  const signOut = () => {
    if (onSignOut) {
      onSignOut();
    }
  };

  const value = {
    isLoaded,
    isSignedIn: !!token,
    user,
    sessionId,
    getToken,
    signOut,
  };

  console.log('[CapacitorAuth] Context value:', { isLoaded, isSignedIn: !!token, hasUser: !!user });

  return (
    <CapacitorAuthContext.Provider value={value}>
      {children}
    </CapacitorAuthContext.Provider>
  );
}

// Custom hook that mimics Clerk's useAuth
export function useCapacitorAuth() {
  const context = useContext(CapacitorAuthContext);
  if (!context) {
    throw new Error('useCapacitorAuth must be used within CapacitorAuthProvider');
  }
  return context;
}

// Custom hook that mimics Clerk's useUser
export function useCapacitorUser() {
  const context = useContext(CapacitorAuthContext);
  if (!context) {
    throw new Error('useCapacitorUser must be used within CapacitorAuthProvider');
  }
  return {
    isLoaded: context.isLoaded,
    isSignedIn: context.isSignedIn,
    user: context.user,
  };
}

export default CapacitorAuthContext;
